import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageSEO } from '../components/SEO';

const HowItWorks = () => {
  return (
    <>
      <PageSEO.HowItWorks />
      <div className="bg-[#FEF7ED] text-[#1e1b4b]" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* ============================= */}
      {/* HERO: Simple Promise */}
      {/* ============================= */}
      <section className="relative pt-32 pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
        {/* Background blobs */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-200/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-200/40 rounded-full blur-3xl"></div>
        </div>

        {/* V5 Gradient Line at Bottom */}
        <div className="absolute bottom-0 left-0 w-full h-1 z-20" style={{ background: 'linear-gradient(to right, #8b5cf6 0%, #f97316 100%)' }}></div>

        <motion.div
          className="max-w-5xl mx-auto px-6 text-center relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* V5 Code Accent */}
          <p className="text-xs mb-4" style={{ fontFamily: "'JetBrains Mono', monospace", color: 'rgba(30, 27, 75, 0.3)' }}>// how_it_works</p>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-orange-200 shadow-sm mb-8">
            <span className="text-2xl">🍺</span>
            <span className="text-sm font-medium text-[#1e1b4b]/70">Discover deals while you&apos;re out</span>
          </div>

          <h1 className="text-5xl md:text-7xl text-[#1e1b4b] mb-6 leading-tight" style={{ fontFamily: "'Faustina', Georgia, serif", fontWeight: 500 }}>
            Scan. Claim. <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">Save.</span>
          </h1>

          <p className="text-xl md:text-2xl text-[#1e1b4b]/60 max-w-2xl mx-auto mb-12">
            Every Tappd coaster is a gateway to local deals.
            No app needed — just your phone camera.
          </p>

          {/* Quick visual: Coaster → Phone → Deal */}
          <div className="flex items-center justify-center gap-4 md:gap-8 flex-wrap">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-md">
              <span className="text-2xl">🧡</span>
              <span className="font-medium">Coaster</span>
            </div>
            <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
            </svg>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-md">
              <span className="text-2xl">📱</span>
              <span className="font-medium">Scan</span>
            </div>
            <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
            </svg>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-md">
              <span className="text-2xl">💰</span>
              <span className="font-medium">Save</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ============================= */}
      {/* STEP 1: SPOT THE COASTER */}
      {/* ============================= */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Visual */}
            <div className="relative order-2 lg:order-1">
              {/* Bar scene with coaster */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=800&q=80"
                  alt="Bar scene"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

                {/* Highlighted coaster overlay */}
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="relative">
                    {/* Coaster card */}
                    <div className="relative bg-white rounded-2xl p-4 shadow-xl animate-bounce" style={{ animationDuration: '3s' }}>
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center border-2 border-dashed border-orange-300">
                          <svg className="w-8 h-8 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h1v1h-1v-1zm-3 0h1v1h-1v-1zm3 3h1v1h-1v-1zm-3 0h1v1h-1v-1zm0 3h1v1h-1v-1zm3 0h1v1h-1v-1z"/>
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs text-orange-600 font-semibold uppercase tracking-wide">Tappd Coaster</div>
                          <div className="text-lg text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif" }}>Scan for local deals</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step badge */}
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center shadow-xl">
                <span className="text-3xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>1</span>
              </div>
            </div>

            {/* Copy */}
            <div className="order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-100 rounded-full">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                <span className="text-sm font-semibold text-orange-700">Step 1</span>
              </div>

              <h2 className="text-4xl md:text-5xl text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Spot a Tappd coaster
              </h2>

              <p className="text-lg text-[#1e1b4b]/60 leading-relaxed">
                You&apos;re at a bar, brewery, or restaurant. You reach for your drink and notice something different about the coaster — it has a QR code and says &quot;Scan for deals.&quot;
              </p>

              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-[#1e1b4b]">50+ partner venues</div>
                    <div className="text-sm text-[#1e1b4b]/50">Bars, breweries, and restaurants across South Jersey</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-[#1e1b4b]">New deals every week</div>
                    <div className="text-sm text-[#1e1b4b]/50">Fresh offers from local businesses</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* STEP 2: SCAN WITH YOUR PHONE */}
      {/* ============================= */}
      <section className="py-24 bg-gradient-to-b from-[#1e1b4b] to-[#2d2a5e] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Copy */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                <span className="text-sm font-semibold text-orange-300">Step 2</span>
              </div>

              <h2 className="text-4xl md:text-5xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                Point your camera & scan
              </h2>

              <p className="text-lg text-white/60 leading-relaxed">
                Open your phone&apos;s camera app — that&apos;s it. No special app to download.
                Point it at the QR code and tap the link that appears. Takes about 2 seconds.
              </p>

              <div className="bg-white/10 rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-white">No app required</div>
                    <div className="text-sm text-white/50">Works with your phone&apos;s built-in camera</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-white">Works on any phone</div>
                    <div className="text-sm text-white/50">iPhone, Android, doesn&apos;t matter</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual: Phone scanning */}
            <div className="relative flex justify-center">
              <div className="relative">
                {/* Phone mockup */}
                <div className="w-[280px] rounded-[40px] p-3 shadow-2xl" style={{ background: 'linear-gradient(145deg, #2d2d2d 0%, #1a1a1a 100%)' }}>
                  <div className="bg-white rounded-[32px] overflow-hidden">
                    <div className="w-[120px] h-7 bg-[#1a1a1a] rounded-b-2xl mx-auto"></div>

                    {/* Camera view */}
                    <div className="relative bg-gray-900 aspect-[9/16]">
                      {/* QR code being scanned */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative">
                          {/* QR code */}
                          <div className="w-32 h-32 bg-white rounded-xl p-3 shadow-2xl">
                            <div className="w-full h-full grid grid-cols-5 gap-1">
                              {[...Array(25)].map((_, i) => (
                                <div key={i} className={`rounded-sm ${Math.random() > 0.4 ? 'bg-[#1e1b4b]' : 'bg-transparent'}`}></div>
                              ))}
                            </div>
                          </div>

                          {/* Corner brackets */}
                          <div className="absolute -inset-4">
                            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-orange-500 rounded-tl"></div>
                            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-orange-500 rounded-tr"></div>
                            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-orange-500 rounded-bl"></div>
                            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-orange-500 rounded-br"></div>
                          </div>
                        </div>
                      </div>

                      {/* Link popup */}
                      <div className="absolute bottom-6 left-4 right-4">
                        <div className="bg-white rounded-xl p-3 shadow-xl flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-500">tappd.app</div>
                            <div className="text-sm font-semibold text-[#1e1b4b] truncate">Open Tappd Deals</div>
                          </div>
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step badge */}
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center shadow-xl">
                  <span className="text-3xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>2</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* STEP 3: CLAIM YOUR DEAL */}
      {/* ============================= */}
      <section className="py-24 bg-[#FEF7ED]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Visual: Deals page on phone */}
            <div className="relative flex justify-center order-2 lg:order-1">
              <div className="relative">
                {/* Phone mockup */}
                <div className="w-[280px] rounded-[40px] p-3 shadow-2xl" style={{ background: 'linear-gradient(145deg, #2d2d2d 0%, #1a1a1a 100%)' }}>
                  <div className="bg-[#FEF7ED] rounded-[32px] overflow-hidden">
                    <div className="w-[120px] h-7 bg-[#1a1a1a] rounded-b-2xl mx-auto"></div>

                    {/* App screen */}
                    <div className="min-h-[500px] p-4">
                      {/* Header */}
                      <div className="text-center mb-4">
                        <div className="text-xs text-[#1e1b4b]/50 uppercase tracking-wide">Deals at</div>
                        <div className="text-lg text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif" }}>Ott&apos;s Tavern Medford</div>
                      </div>

                      {/* Deal cards */}
                      <div className="space-y-3">
                        {/* Deal 1 - Featured */}
                        <div className="bg-white rounded-xl p-3 shadow-md border-2 border-orange-300 relative overflow-hidden">
                          <div className="absolute top-0 right-0 px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded-bl-lg">HOT</div>
                          <div className="flex gap-3">
                            <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-amber-100 rounded-lg flex items-center justify-center text-2xl">🍕</div>
                            <div className="flex-1">
                              <div className="text-xs text-orange-600 font-semibold">Angelo&apos;s Pizza</div>
                              <div className="font-semibold text-[#1e1b4b] text-sm">Free appetizer</div>
                              <div className="text-xs text-[#1e1b4b]/50">with any large pie</div>
                            </div>
                          </div>
                          <button className="mt-2 w-full py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-2">
                            Claim Deal
                            <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-xs">+25</span>
                          </button>
                        </div>

                        {/* Deal 2 */}
                        <div className="bg-white rounded-xl p-3 shadow-sm">
                          <div className="flex gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg flex items-center justify-center text-xl">💇</div>
                            <div className="flex-1">
                              <div className="text-xs text-green-600 font-semibold">Classic Cuts</div>
                              <div className="font-semibold text-[#1e1b4b] text-sm">20% off first visit</div>
                            </div>
                            <button className="px-3 py-1 border border-orange-300 rounded-lg text-orange-600 text-xs font-semibold">
                              Claim
                            </button>
                          </div>
                        </div>

                        {/* Deal 3 */}
                        <div className="bg-white rounded-xl p-3 shadow-sm">
                          <div className="flex gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-lg flex items-center justify-center text-xl">🚗</div>
                            <div className="flex-1">
                              <div className="text-xs text-blue-600 font-semibold">SJ Auto Spa</div>
                              <div className="font-semibold text-[#1e1b4b] text-sm">$10 off detail</div>
                            </div>
                            <button className="px-3 py-1 border border-orange-300 rounded-lg text-orange-600 text-xs font-semibold">
                              Claim
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step badge */}
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center shadow-xl">
                  <span className="text-3xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>3</span>
                </div>

                {/* Floating claimed badge */}
                <div className="absolute -bottom-4 -left-8 bg-white rounded-xl px-4 py-3 shadow-xl border border-green-200 animate-bounce" style={{ animationDuration: '2s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs text-green-600 font-semibold">Deal Claimed!</div>
                      <div className="text-xs text-[#1e1b4b]/50">Saved to your phone</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Copy */}
            <div className="space-y-6 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-100 rounded-full">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                <span className="text-sm font-semibold text-orange-700">Step 3</span>
              </div>

              <h2 className="text-4xl md:text-5xl text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Browse & claim your deal
              </h2>

              <p className="text-lg text-[#1e1b4b]/60 leading-relaxed">
                Instantly see all the deals available near you. Tap &quot;Claim&quot; on any offer
                that catches your eye — it&apos;s saved to your phone and ready to use.
              </p>

              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-[#1e1b4b]">Seven days to use it</div>
                    <div className="text-sm text-[#1e1b4b]/50">Deals stay in your wallet for 7 days after you claim</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-[#1e1b4b]">Real savings</div>
                    <div className="text-sm text-[#1e1b4b]/50">Discounts and freebies from businesses near you</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-[#1e1b4b]">Hyperlocal businesses</div>
                    <div className="text-sm text-[#1e1b4b]/50">Support shops within 15 miles of where you&apos;re sitting</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* STEP 4: REDEEM & SAVE */}
      {/* ============================= */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Copy */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-full">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-sm font-semibold text-green-700">Step 4</span>
              </div>

              <h2 className="text-4xl md:text-5xl text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Redeem & <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">save</span>
              </h2>

              <p className="text-lg text-[#1e1b4b]/60 leading-relaxed">
                Visit the business, show your deal code, and enjoy your savings.
                Show your 6-character code at the business and enjoy the deal. That's it — no app to install, nothing to print.</p>

            </div>

            {/* Visual: Redemption success */}
            <div className="relative flex justify-center">
              <div className="relative">
                {/* Success card */}
                <div className="bg-white rounded-3xl p-8 shadow-2xl border border-green-200 max-w-sm">
                  {/* Checkmark */}
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>

                  <div className="text-center mb-6">
                    <h3 className="text-2xl text-[#1e1b4b] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Deal Redeemed!</h3>
                    <p className="text-[#1e1b4b]/60">You saved $8.99 at Angelo&apos;s Pizza</p>
                  </div>

                </div>

                {/* Step badge */}
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-xl">
                  <span className="text-3xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>4</span>
                </div>

                {/* Confetti elements */}
                <div className="absolute -top-8 left-1/4 text-2xl animate-bounce" style={{ animationDelay: '0s', animationDuration: '2s' }}>🎉</div>
                <div className="absolute -top-4 right-1/4 text-2xl animate-bounce" style={{ animationDelay: '-1s', animationDuration: '2.5s' }}>✨</div>
                <div className="absolute top-1/4 -right-8 text-2xl animate-bounce" style={{ animationDelay: '-0.5s', animationDuration: '3s' }}>🎊</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* SUMMARY: Quick Recap */}
      {/* ============================= */}
      <section className="py-20 bg-gradient-to-b from-[#FEF7ED] to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl text-[#1e1b4b] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>That&apos;s it. Really.</h2>
            <p className="text-xl text-[#1e1b4b]/60">Four steps to local savings</p>
          </div>

          {/* Horizontal steps */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-3xl">👀</span>
              </div>
              <div className="font-semibold text-[#1e1b4b] mb-1">1. Spot</div>
              <div className="text-sm text-[#1e1b4b]/50">See a coaster</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-3xl">📱</span>
              </div>
              <div className="font-semibold text-[#1e1b4b] mb-1">2. Scan</div>
              <div className="text-sm text-[#1e1b4b]/50">Use your camera</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-3xl">🎯</span>
              </div>
              <div className="font-semibold text-[#1e1b4b] mb-1">3. Claim</div>
              <div className="text-sm text-[#1e1b4b]/50">Pick a deal</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-3xl">💰</span>
              </div>
              <div className="font-semibold text-[#1e1b4b] mb-1">4. Save</div>
              <div className="text-sm text-[#1e1b4b]/50">Redeem your code</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* CTA: Find Deals */}
      {/* ============================= */}
      <section className="py-24 bg-gradient-to-br from-[#1e1b4b] to-[#2d2a5e] text-white relative overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=1920" alt="" className="w-full h-full object-cover opacity-20"/>
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ready to start <span className="text-orange-400">saving?</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto mb-10">
            Find a Tappd coaster at your favorite local bar, or browse deals online right now.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/local-deals-near-me" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full text-lg font-semibold shadow-xl hover:shadow-orange-500/50 transition-all">
              Browse Deals Near Me
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
              </svg>
            </Link>
            <Link to="/for-businesses" className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 rounded-full text-lg font-semibold hover:bg-white/10 transition-all">
              I&apos;m a Business Owner
            </Link>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-12 text-white/50 text-sm">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
              </svg>
              No app download
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
              </svg>
              100% free for you
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
              </svg>
              Real local savings
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default HowItWorks;
